import React, { useState, useRef, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';
import { VendorCard, ProgressCard, TimelineCard } from './VowBotMessageCards';
import { generateSmartQuickActions } from '../utils/vowbotFormatters';
import VendorProfileModal from './VendorProfileModal';
import NotificationModal from './NotificationModal';
import { formatVendorForModal } from '../utils/vendorHelpers';

// API configuration - using centralized config

const VowBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi! I'm VowBot, your AI wedding planning assistant. How can I help make your special day perfect? 💕",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [userContext, setUserContext] = useState(null);
  const [contextLoading, setContextLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Vendor profile modal state
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  
  // Notification state
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'success',
    title: '',
    message: ''
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // NEW: Load conversation from localStorage on mount
  useEffect(() => {
    const savedMessages = localStorage.getItem('vowbot_messages');
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages);
        setMessages(parsed);
      } catch (error) {
        console.error('Failed to load saved messages:', error);
      }
    }
  }, []);

  // NEW: Save conversation to localStorage whenever messages change
  useEffect(() => {
    if (messages.length > 1) { // Don't save if only welcome message
      localStorage.setItem('vowbot_messages', JSON.stringify(messages));
    }
  }, [messages]);

  // NEW: Sync conversation across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'vowbot_messages' && e.newValue) {
        try {
          const newMessages = JSON.parse(e.newValue);
          setMessages(newMessages);
        } catch (error) {
          console.error('Failed to sync messages:', error);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // NEW: Load user context on mount
  useEffect(() => {
    loadUserContext();
  }, []);

  // NEW: Listen for login events (when user logs in via AuthModal)
  useEffect(() => {
    const handleStorageChange = () => {
      loadUserContext(); // Reload userId when storage changes
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom login event
    const handleLogin = (event) => {
      if (event.detail && event.detail.userId) {
        setUserId(event.detail.userId);
        localStorage.setItem('userId', event.detail.userId);
      }
    };
    
    // Listen for logout event
    const handleLogout = () => {
      // Clear conversation
      const initialMessage = {
        id: 1,
        text: "Hi! I'm VowBot, your AI wedding planning assistant. How can I help make your special day perfect? 💕",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages([initialMessage]);
      localStorage.removeItem('vowbot_messages');
      
      // Clear user context
      setUserId(null);
      setUserContext(null);
    };
    
    window.addEventListener('userLoggedIn', handleLogin);
    window.addEventListener('userLoggedOut', handleLogout);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLoggedIn', handleLogin);
      window.removeEventListener('userLoggedOut', handleLogout);
    };
  }, []);

  // NEW: Load user context function
  const loadUserContext = async () => {
    try {
      // Try to get userId from localStorage or auth
      let storedUserId = localStorage.getItem('userId');
      
      if (!storedUserId) return;

      setUserId(storedUserId);
      setContextLoading(true);

      // Load context from backend (optional - for now we'll skip this)
      // In production, you'd call an API to get user context
      // const response = await fetch(`${API_BASE_URL}/user/context/${storedUserId}`);
      // const data = await response.json();
      // setUserContext(data.context);

      setContextLoading(false);
    } catch (error) {
      console.error('Failed to load user context:', error);
      setContextLoading(false);
    }
  };

  // Vendor profile modal handlers
  const handleVendorClick = (vendor) => {
    const profileData = formatVendorForModal(vendor);
    setSelectedVendor(profileData);
    setShowProfile(true);
  };

  const showNotification = (type, title, message) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message
    });
  };

  const closeNotification = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  // Format message text with markdown-like syntax
  const formatMessageText = (text) => {
    if (!text) return '';
    
    return text
      // Headers
      .replace(/### (.*?)(\n|$)/g, '<h4 class="text-base font-bold text-gray-900 mt-3 mb-2">$1</h4>')
      .replace(/## (.*?)(\n|$)/g, '<h3 class="text-lg font-bold text-gray-900 mt-4 mb-2">$1</h3>')
      
      // Bold text
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-gray-900">$1</strong>')
      
      // Bullet points
      .replace(/^• (.*?)$/gm, '<div class="flex items-start ml-2 mb-1"><span class="text-rose-500 mr-2">•</span><span>$1</span></div>')
      
      // Numbered lists
      .replace(/^(\d+)\.\s+(.*?)$/gm, '<div class="flex items-start mb-2"><span class="bg-gradient-to-r from-rose-500 to-pink-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">$1</span><span>$2</span></div>')
      
      // Paragraphs (double line breaks)
      .replace(/\n\n/g, '<div class="h-3"></div>')
      
      // Single line breaks
      .replace(/\n/g, '<br>');
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentMessage = inputMessage;
    setInputMessage('');
    setIsTyping(true);

    try {
      // Build conversation history from messages (exclude system message)
      const conversationHistory = messages
        .filter(m => m.sender !== 'system')
        .map(m => ({
          role: m.sender === 'user' ? 'user' : 'assistant',
          content: m.text
        }));

      // Call the real chat API with userId for context-aware responses
      const response = await fetch(`${API_BASE_URL}/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: currentMessage,
          tone: 'friendly',
          language: 'en',
          context: {},
          conversation_history: conversationHistory,
          userId: userId
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Check if authentication is required
        if (data.requiresAuth) {
          const botResponse = {
            id: Date.now() + 1,
            text: data.response,
            sender: 'bot',
            timestamp: new Date(),
            requiresAuth: true,
            suggestedActions: data.suggestedActions
          };
          setMessages(prev => [...prev, botResponse]);
        } else {
          const botResponse = {
            id: Date.now() + 1,
            text: data.response || 'I apologize, but I encountered an issue. How else can I help you with your wedding planning?',
            sender: 'bot',
            timestamp: new Date(),
            metadata: data.metadata,
            cardData: data.cardData,
            suggestedActions: data.suggestedActions
          };
          setMessages(prev => [...prev, botResponse]);
        }
      } else {
        throw new Error('API call failed');
      }
    } catch (error) {
      console.error('VowBot API error:', error);
      // Fallback to intelligent responses
      const botResponse = {
        id: Date.now() + 1,
        text: getIntelligentResponse(currentMessage),
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
    }
    
    setIsTyping(false);
  };

  const getIntelligentResponse = (message) => {
    const lowerMessage = message.toLowerCase();
    
    // Budget-related responses
    if (lowerMessage.includes('budget') || lowerMessage.includes('cost') || lowerMessage.includes('money') || lowerMessage.includes('price')) {
      return "I'd love to help you with your wedding budget! 💰 Our Budget Allocator can help you plan your finances effectively. You can set your total budget and I'll help you allocate it across different categories like venue, catering, photography, and more. Would you like me to guide you through creating a budget plan?";
    }
    
    // Vendor-related responses
    if (lowerMessage.includes('vendor') || lowerMessage.includes('supplier') || lowerMessage.includes('photographer') || lowerMessage.includes('caterer') || lowerMessage.includes('florist')) {
      return "Finding the right vendors is crucial for your perfect day! 🏪 Our Vendor Vault has an extensive database of wedding professionals. I can help you find photographers, caterers, florists, musicians, and more. You can also use our Smart Vendor Matching to get personalized recommendations. What type of vendor are you looking for?";
    }
    
    // Timeline-related responses
    if (lowerMessage.includes('timeline') || lowerMessage.includes('schedule') || lowerMessage.includes('plan') || lowerMessage.includes('organize')) {
      return "Great question about planning! 📅 Our Timeline Generator can help you create a detailed wedding timeline. From 12 months before to your wedding day, I'll help you organize every detail - from booking venues to final preparations. Would you like me to help you create a personalized timeline?";
    }
    
    // Design/theme responses
    if (lowerMessage.includes('theme') || lowerMessage.includes('design') || lowerMessage.includes('color') || lowerMessage.includes('style') || lowerMessage.includes('decoration')) {
      return "I love helping with wedding design! 🎨 Our Dream Wedding Generator can create personalized themes based on your vision, and our Moodboard Matcher helps you visualize everything perfectly. What style are you envisioning - classic elegance, bohemian chic, modern minimalist, or something else?";
    }
    
    // Invitation responses
    if (lowerMessage.includes('invitation') || lowerMessage.includes('invite') || lowerMessage.includes('card') || lowerMessage.includes('stationery')) {
      return "Wedding invitations set the tone for your special day! 💌 Our Invitation Designer can help you create beautiful, personalized invitations that match your wedding theme. From elegant classics to modern designs, we have templates for every style. Would you like to explore some invitation options?";
    }
    
    // Weather responses
    if (lowerMessage.includes('weather') || lowerMessage.includes('outdoor') || lowerMessage.includes('rain') || lowerMessage.includes('backup')) {
      return "Weather planning is so important for outdoor weddings! 🌤️ Our Weather Risk Advisor can provide forecasts for your wedding date and location, plus give you practical recommendations for backup plans. I can help you prepare for any weather scenario to ensure your day is perfect!";
    }
    
    // Cultural/tradition responses
    if (lowerMessage.includes('tradition') || lowerMessage.includes('cultural') || lowerMessage.includes('ritual') || lowerMessage.includes('ceremony')) {
      return "Incorporating meaningful traditions makes weddings so special! 📖 Our Cultural Ritual Explainer covers wedding traditions from around the world - from Indian Saptapadi to Jewish glass breaking, Chinese tea ceremonies to Celtic handfasting. What cultural elements would you like to include in your ceremony?";
    }
    
    // Thank you note responses
    if (lowerMessage.includes('thank you') || lowerMessage.includes('gratitude') || lowerMessage.includes('note') || lowerMessage.includes('appreciation')) {
      return "Expressing gratitude is such a beautiful part of the wedding journey! 💕 Our Thank You Note Generator can help you create personalized thank you notes for all your guests. I can help you craft heartfelt messages that truly capture your appreciation. Would you like to start creating some thank you notes?";
    }
    
    // General wedding planning
    if (lowerMessage.includes('wedding') || lowerMessage.includes('marry') || lowerMessage.includes('bride') || lowerMessage.includes('groom')) {
      return "Congratulations on your upcoming wedding! 💕 I'm here to help make your special day absolutely perfect. I can assist you with budget planning, vendor selection, timeline creation, design inspiration, and so much more. What aspect of your wedding planning would you like to focus on first?";
    }
    
    // Default helpful response
    return "I'm here to help make your wedding dreams come true! ✨ I can assist you with budget planning, finding vendors, creating timelines, designing invitations, checking weather forecasts, exploring cultural traditions, and much more. What would you like to work on together?";
  };

  // Quick actions - simplified and focused
  const quickActions = [
    { text: "Help me get started", icon: "🌟", gradient: "from-purple-400 to-pink-500" },
    { text: "Find Vendors", icon: "🏪", gradient: "from-blue-400 to-cyan-500" },
    { text: "Plan my budget", icon: "💰", gradient: "from-emerald-400 to-teal-500" },
    { text: "Create timeline", icon: "📅", gradient: "from-orange-400 to-red-500" }
  ];

  const handleQuickAction = async (actionText) => {
    const userMessage = {
      id: Date.now(),
      text: actionText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      // Build conversation history from messages
      const conversationHistory = messages
        .filter(m => m.sender !== 'system')
        .map(m => ({
          role: m.sender === 'user' ? 'user' : 'assistant',
          content: m.text
        }));

      // Call the real chat API for quick actions too
      const response = await fetch(`${API_BASE_URL}/chat/message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: actionText,
          tone: 'friendly',
          language: 'en',
          context: {},
          conversation_history: conversationHistory,
          userId: userId
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Check if authentication is required
        if (data.requiresAuth) {
          const botResponse = {
            id: Date.now() + 1,
            text: data.response,
            sender: 'bot',
            timestamp: new Date(),
            requiresAuth: true,
            suggestedActions: data.suggestedActions
          };
          setMessages(prev => [...prev, botResponse]);
        } else {
          const botResponse = {
            id: Date.now() + 1,
            text: data.response || getIntelligentResponse(actionText),
            sender: 'bot',
            timestamp: new Date(),
            cardData: data.cardData,
            suggestedActions: data.suggestedActions
          };
          setMessages(prev => [...prev, botResponse]);
        }
      } else {
        throw new Error('API call failed');
      }
    } catch (error) {
      console.error('VowBot quick action API error:', error);
      // Fallback to intelligent response
      const botResponse = {
        id: Date.now() + 1,
        text: getIntelligentResponse(actionText),
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
    }
    
    setIsTyping(false);
  };

  const clearChat = () => {
    const welcomeMessage = [
      {
        id: 1,
        text: "Hi! I'm VowBot, your AI wedding planning assistant. How can I help make your special day perfect? 💕",
        sender: 'bot',
        timestamp: new Date()
      }
    ];
    setMessages(welcomeMessage);
    setInputMessage('');
    localStorage.removeItem('vowbot_messages'); // Clear saved conversation
  };

  return (
    <>
      {/* Chat Widget Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="group bg-gradient-to-r from-rose-500 via-pink-500 to-purple-600 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 flex items-center justify-center animate-pulse"
        >
          {isOpen ? (
            <svg className="w-6 h-6 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <div className="flex items-center space-x-2">
              <span className="text-2xl group-hover:animate-bounce">🤖</span>
              <span className="hidden sm:block font-bold text-lg">VowBot</span>
            </div>
          )}
        </button>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-80 sm:w-96 h-[32rem] bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-white/50 z-50 flex flex-col overflow-hidden animate-fadeInScale">
          
          {/* Header */}
          <div className="bg-gradient-to-r from-rose-500 via-pink-500 to-purple-600 text-white p-6 rounded-t-3xl relative overflow-hidden">
            {/* Decorative elements */}
            <div className="absolute top-2 left-2 text-white/20 text-2xl animate-pulse">✨</div>
            <div className="absolute top-2 right-2 text-white/20 text-2xl animate-pulse delay-500">💕</div>
            <div className="absolute bottom-2 left-2 text-white/20 text-xl animate-pulse delay-1000">🌸</div>
            
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center animate-bounce">
                  <span className="text-2xl">🤖</span>
                </div>
                <div>
                  <h3 className="font-serif font-bold text-xl">VowBot</h3>
                  <p className="text-sm opacity-90 font-light">Your AI Wedding Assistant</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={clearChat}
                  className="text-white hover:bg-white/20 p-2 rounded-full transition-all duration-300 hover:rotate-12"
                  title="Clear Chat"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-full transition-all duration-300 hover:rotate-90"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gradient-to-br from-rose-25 via-pink-25 to-purple-25">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} animate-fadeIn`}
              >
                <div className={`${message.sender === 'user' ? 'max-w-xs' : 'max-w-2xl w-full'}`}>
                  <div
                    className={`px-4 py-3 rounded-2xl text-sm font-medium shadow-lg ${
                      message.sender === 'user'
                        ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white'
                        : 'bg-white/80 backdrop-blur-sm text-gray-800 border border-white/50'
                    }`}
                  >
                    {message.sender === 'bot' ? (
                      <div 
                        className="prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: formatMessageText(message.text) }}
                      />
                    ) : (
                      message.text
                    )}
                  </div>
                  
                  {/* Render cards if cardData exists */}
                  {message.cardData && message.sender === 'bot' && (
                    <div className="mt-3 space-y-2">
                      {message.cardData.vendors && message.cardData.vendors.length > 0 && (
                        <>
                          <div className="space-y-2">
                            {message.cardData.vendors.slice(0, 3).map((vendor, idx) => (
                              <VendorCard 
                                key={idx} 
                                vendor={vendor} 
                                onViewProfile={handleVendorClick}
                              />
                            ))}
                          </div>
                          
                          {/* Show overflow message if more than 3 vendors */}
                          {message.cardData.vendors.length > 3 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              <div className="text-xs text-gray-600 flex items-center">
                                Showing 3 of {message.cardData.vendors.length} vendors
                              </div>
                              <button
                                onClick={() => window.open('/vendor-vault/vendor-shortlisting-assistant', '_blank')}
                                className="bg-gradient-to-r from-rose-100 to-pink-100 hover:from-rose-200 hover:to-pink-200 text-gray-800 px-3 py-2 rounded-lg text-xs font-medium shadow-sm hover:shadow-md transition-all duration-300 flex items-center space-x-1"
                              >
                                <span>🏪</span>
                                <span>View all in Vendor Shortlisting Assistant</span>
                              </button>
                            </div>
                          )}
                        </>
                      )}
                      
                      {message.cardData.progress && (
                        <ProgressCard context={message.cardData.progress} />
                      )}
                      
                      {message.cardData.timeline && message.cardData.timeline.length > 0 && (
                        <>
                          <div className="space-y-2">
                            {message.cardData.timeline.slice(0, 3).map((task, idx) => (
                              <TimelineCard key={idx} task={task} />
                            ))}
                          </div>
                          
                          {/* Show overflow message if more than 3 timeline tasks */}
                          {message.cardData.timeline.length > 3 && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              <div className="text-xs text-gray-600 flex items-center">
                                Showing 3 of {message.cardData.timeline.length} tasks
                              </div>
                              <button
                                onClick={() => window.open('/wedding-blueprint/timeline-generator', '_blank')}
                                className="bg-gradient-to-r from-rose-100 to-pink-100 hover:from-rose-200 hover:to-pink-200 text-gray-800 px-3 py-2 rounded-lg text-xs font-medium shadow-sm hover:shadow-md transition-all duration-300 flex items-center space-x-1"
                              >
                                <span>📅</span>
                                <span>View full timeline in Timeline Generator</span>
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Render suggested actions if available */}
                  {message.suggestedActions && message.suggestedActions.length > 0 && message.sender === 'bot' && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {message.suggestedActions.map((action, idx) => (
                        <button
                          key={idx}
                          onClick={() => {
                            // Handle different action types
                            if (action.isPageRedirect && action.url) {
                              // Open page in new tab
                              window.open(action.url, '_blank');
                            } else if (action.query === '/login' || action.query === '/signup') {
                              // Trigger auth modal
                              window.dispatchEvent(new CustomEvent('openAuthModal', { 
                                detail: { mode: action.query === '/signup' ? 'signup' : 'login' }
                              }));
                            } else if (action.query) {
                              // Send as new query
                              handleQuickAction(action.query);
                            }
                          }}
                          className="bg-gradient-to-r from-rose-100 to-pink-100 hover:from-rose-200 hover:to-pink-200 text-gray-800 px-3 py-2 rounded-lg text-xs font-medium shadow-sm hover:shadow-md transition-all duration-300 flex items-center space-x-1"
                        >
                          <span>{action.icon}</span>
                          <span>{action.text}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start animate-fadeIn">
                <div className="bg-white/80 backdrop-blur-sm text-gray-800 px-4 py-3 rounded-2xl text-sm border border-white/50 shadow-lg">
                  <div className="flex space-x-1 items-center">
                    <span className="text-xs text-gray-500 mr-2">VowBot is typing</span>
                    <div className="w-2 h-2 bg-rose-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length === 1 && (
            <div className="px-6 pb-4 bg-gradient-to-br from-rose-25 via-pink-25 to-purple-25">
              <div className="flex items-center mb-3">
                <span className="text-lg mr-2">✨</span>
                <p className="text-sm font-semibold text-gray-700">Quick Actions</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickAction(action.text)}
                    className={`group flex items-center space-x-2 p-3 bg-gradient-to-r ${action.gradient} text-white rounded-xl hover:shadow-xl transform hover:scale-105 transition-all duration-300 font-medium text-sm`}
                  >
                    <span className="text-lg group-hover:animate-bounce">{action.icon}</span>
                    <span className="truncate">{action.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSendMessage} className="p-6 bg-white/60 backdrop-blur-sm border-t border-white/50 rounded-b-3xl">
            <div className="flex space-x-3">
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  placeholder="Ask me anything about your wedding..."
                  className="w-full px-4 py-3 border-2 border-gold-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-rose-200 focus:border-rose-400 transition-all duration-300 text-sm font-medium bg-white/80 backdrop-blur-sm"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gold-400">
                  <span className="text-lg">💬</span>
                </div>
              </div>
              <button
                type="submit"
                disabled={!inputMessage.trim()}
                className="bg-gradient-to-r from-rose-500 to-pink-500 text-white p-3 rounded-xl hover:from-rose-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>
          </form>
        </div>
      )}

      <style jsx>{`
        @keyframes fadeInScale {
          from {
            opacity: 0;
            transform: scale(0.8) translateY(20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeInScale {
          animation: fadeInScale 0.4s ease-out;
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>

      {/* Vendor Profile Modal */}
      <VendorProfileModal
        isOpen={showProfile}
        onClose={() => setShowProfile(false)}
        vendor={selectedVendor}
        source="matching"
        onShowNotification={showNotification}
      />

      {/* Notification Modal */}
      <NotificationModal
        isOpen={notification.isOpen}
        onClose={closeNotification}
        type={notification.type}
      actionLabel={notification.actionLabel}
      onAction={notification.onAction}
        title={notification.title}
        message={notification.message}
      />
    </>
  );
};

export default VowBot;
